<script setup>
import { tv } from 'tailwind-variants';
import { computed, useSlots } from 'vue';
import { CsIcon } from '../icon';

/**
 * @typedef {object} BannerProps
 * @property {('info'|'success'|'warning'|'critical'|'contrast')} [variantColor='critical'] - Color variant of the banner
 * @property {boolean} [isDismissible=false] - Controls whether the banner can be dismissed
 */

/**
 * @type {BannerProps}
 */
const props = defineProps({
  variantColor: {
    type: String,
    default: 'critical',
  },
  isDismissible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['dismiss']);

const bannerRecipe = tv({
  slots: {
    root: 'pr-1 pl-2 min-h-9 rounded-lg flex items-center',
    content: 'w-full flex gap-2 py-2',
    iconContainer: 'flex-none',
    icon: 'h-5 w-5',
    messageContainer: 'flex items-center',
    message: 'text-sm',
    dismissibleIcon:
      'flex-none h-7 w-7 rounded-lg flex items-center justify-center cursor-pointer',
  },
  variants: {
    variantColor: {
      info: {
        root: 'bg-brand-azure-3',
        icon: 'text-brand-azure-14',
        message: 'text-brand-azure-14',
        link: 'text-brand-azure-14',
        dismissibleIcon: [
          'text-brand-azure-12 hover:bg-brand-azure-5',
          'dark:hover:bg-brand-background-secondary',
          'active:bg-brand-azure-6 transition-colors',
        ],
      },
      success: {
        root: 'bg-brand-green-3',
        icon: 'text-brand-green-14',
        message: 'text-brand-green-14',
        link: 'text-brand-green-14',
        dismissibleIcon: [
          'text-brand-green-14 hover:bg-brand-green-5',
          'dark:hover:bg-brand-background-secondary',
          'active:bg-brand-green-6 transition-colors',
        ],
      },
      warning: {
        root: 'bg-brand-orange-3',
        icon: 'text-brand-orange-14',
        message: 'text-brand-orange-14',
        link: 'text-brand-orange-14',
        dismissibleIcon: [
          'text-brand-orange-14 hover:bg-brand-orange-5',
          'dark:hover:bg-brand-background-secondary',
          'active:bg-brand-orange-6 transition-colors',
        ],
      },
      critical: {
        root: 'bg-brand-red-4',
        icon: 'text-brand-red-12',
        message: 'text-brand-red-14',
        link: 'text-brand-red-14',
        dismissibleIcon: [
          'text-brand-red-12 hover:bg-brand-red-5',
          'dark:hover:bg-brand-background-secondary',
          'active:bg-brand-red-6 transition-colors',
        ],
      },
      contrast: {
        root: 'bg-brand-gray-16',
        icon: 'text-white',
        message: 'text-white',
        link: 'text-white',
        dismissibleIcon: [
          'text-white hover:bg-brand-gray-15',
          'dark:hover:bg-brand-background-secondary',
          'active:bg-brand-gray-14 transition-colors',
        ],
      },
    },
  },
});

const bannerClasses = computed(() => bannerRecipe(props));

const slots = useSlots();

function hasSlot(name) {
  return !!slots[name];
}

const getVariantIcon = computed(() => {
  if (props.variantColor === 'critical') {
    return 'alert-diamond';
  }

  if (props.variantColor === 'success') {
    return 'check';
  }

  if (props.variantColor === 'warning') {
    return 'alert-triangle';
  }

  if (props.variantColor === 'info') {
    return 'info';
  }

  return 'info';
});
</script>

<template>
  <div
    data-component="CsBanner"
    data-slot="root"
    :data-variant-color="props.variantColor"
    :class="bannerClasses.root()"
  >
    <div data-slot="content" :class="bannerClasses.content()">
      <div data-slot="icon-container" :class="bannerClasses.iconContainer()">
        <slot v-if="hasSlot('icon')" name="icon" />
        <CsIcon
          v-else
          :name="getVariantIcon"
          data-slot="icon"
          :class="bannerClasses.icon()"
        />
      </div>

      <div
        data-slot="message-container"
        :class="bannerClasses.messageContainer()"
      >
        <span :class="bannerClasses.message()">
          <slot />
        </span>
      </div>
    </div>

    <button
      v-if="props.isDismissible"
      :class="bannerClasses.dismissibleIcon()"
      @click="emit('dismiss')"
    >
      <CsIcon name="x" class="h-5 w-5" />
    </button>
  </div>
</template>
